// @ts-nocheck
/* eslint-disable */

"use client";

import {
  Button,
  Column,
  FormField,
  Heading,
  Icon,
  RichText,
  Row,
  Separator,
} from "daskapital-ds";
import React, { useEffect, useState } from "react";
import {
  StyledBubble,
  calculateRentInvestorPeriodPayment,
  calculateRentInvestorTotalMonths,
  calculateRentInvestorTotalReceived,
  calculateRentInvestorUpfrontBonus,
  formatAsCurrency,
  formsAttrs,
} from "./simulator.utils";

import Link from "next/link";
import { NumericFormat } from "react-number-format";
import ReactDOMServer from "react-dom/server";
import { useGlobalContext } from "@/lib/globalContext";
import Newsletter from "../Newsletter";

export default function InvestRent({
  getStarted,
  knowMore,
  title,
  description,
  backgroundColor,
  textColor,
  termsDescription,
  primaryButtonText,
  secondaryButtonText,
  showNotifyMe,
}: formsAttrs) {
  const state = useGlobalContext();
  const shouldShowNotifyMe = showNotifyMe ?? false;

  // Rent frequency is fixed to monthly for now.
  const rentFrequency = "MONTHLY";

  const [fields, setFields] = useState<Record<string, string | number>>({
    amount: 0,
    numberOfRents: 3,
    minReturnRate: 15,
  });
  const [periodPayment, setPeriodPayment] = useState<number>(0);
  const [upfrontBonus, setUpfrontBonus] = useState<number>(0);
  const [totalReceived, setTotalReceived] = useState<number>(0);
  const [totalMonths, setTotalMonths] = useState<number>(0);

  const setValue = (key: string, value: string | number) => {
    setFields({ ...fields, [key]: value });
  };

  const calcTotal = () => {
    setTotalMonths(
      calculateRentInvestorTotalMonths({
        numberOfRents: Number(fields.numberOfRents),
        rentFrequency,
      }),
    );

    if (!fields.amount || !fields.numberOfRents || !fields.minReturnRate) {
      setPeriodPayment(0);
      setUpfrontBonus(0);
      setTotalReceived(0);
      return;
    }

    const params = {
      amount: Number(fields.amount),
      numberOfRents: Number(fields.numberOfRents),
      rentFrequency,
      minReturnRate: Number(fields.minReturnRate),
    };

    setPeriodPayment(calculateRentInvestorPeriodPayment(params));
    setUpfrontBonus(calculateRentInvestorUpfrontBonus(params));
    setTotalReceived(calculateRentInvestorTotalReceived(params));
  };

  useEffect(() => {
    calcTotal();
  }, [fields]);

  const linkTerms = ReactDOMServer.renderToStaticMarkup(
    <Link href={`/${state.lang}/contact-us/`} className="color-primary50">
      {state?.dict?.common?.knowMore}
    </Link>,
  );

  return (
    <div
      className="block-group radius-md"
      style={{
        padding: "16px",
        overflow: "hidden",
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(textColor ? { color: textColor } : {}),
      }}
    >
      <Row fluid={true} className="m-0">
        <Column lg={8}>
          <div className="mb-8 mb-md-0 pe-md-8">
            <Heading size={"h3"} tag={"h4"}>
              {title || state?.dict?.custom?.investRent}
            </Heading>
            <Heading size={"texts"} tag={"span"} className="font-alternative mt-3">
              {description || state?.dict?.custom?.investRentTxt}
            </Heading>

            {shouldShowNotifyMe && (
              <div className="d-flex bg-gray10 radius-sm p-4 mt-5">
                <Newsletter
                  label={state?.dict?.custom?.notifyAvailableTitle}
                  placeholder={state?.dict?.custom?.notifyAvailablePlaceholder}
                  buttonText={state?.dict?.custom?.notifyAvailableButton}
                />
              </div>
            )}

            <div className="mt-7">
              <Heading size={"texts"} tag={"span"} className="font-alternative">
                {state?.dict?.custom?.amountTitleInvestRent}
              </Heading>
              <div className="mt-2">
                <FormField id="amount" className="mb-0">
                  <NumericFormat
                    thousandSeparator=" "
                    decimalSeparator=","
                    suffix={"€"}
                    decimalScale={2}
                    allowNegative={false}
                    name="amount"
                    placeholder={state?.dict?.custom?.amount}
                    className="font-alternative w-100"
                    onChange={(e) => {
                      setValue(
                        "amount",
                        parseFloat(
                          e.target.value.replaceAll(" ", "").replaceAll(",", "."),
                        ),
                      );
                    }}
                  />
                </FormField>
              </div>
            </div>

            <div className="mt-6">
              <Heading size={"texts"} tag={"span"} className="font-alternative">
                {state?.dict?.custom?.numberOfRentsTitleInvestRent}
              </Heading>
              <div className="d-flex align-items-center mt-2" style={{ gap: "1rem" }}>
                <div className="flex-grow-1">
                  <FormField id="numberOfRents" className="mb-0">
                    <input
                      type={"range"}
                      name="numberOfRents"
                      className="w-100"
                      min={3}
                      max={12}
                      step={1}
                      defaultValue={3}
                      onChange={(e) => {
                        e.preventDefault();
                        setValue("numberOfRents", Number(e.target.value));
                      }}
                    />
                  </FormField>
                </div>
                <div className="flex-shrink-1">
                  <StyledBubble type="text" className="texts">
                    {fields.numberOfRents}
                  </StyledBubble>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <Heading size={"texts"} tag={"span"} className="font-alternative">
                {state?.dict?.custom?.minReturnRateTitle}
              </Heading>
              <div className="d-flex align-items-center mt-2" style={{ gap: "1rem" }}>
                <div className="flex-grow-1">
                  <FormField id="minReturnRate" className="mb-0">
                    <input
                      type={"range"}
                      name="minReturnRate"
                      className="w-100"
                      min={5}
                      max={30}
                      step={0.1}
                      defaultValue={15}
                      onChange={(e) => {
                        setValue(
                          "minReturnRate",
                          Math.round(Number(e.target.value) * 10) / 10,
                        );
                      }}
                    />
                  </FormField>
                </div>
                <div className="flex-shrink-1">
                  <StyledBubble type="text" className="texts">
                    {fields.minReturnRate}%
                  </StyledBubble>
                </div>
              </div>
            </div>

            <div className="d-flex bg-gray10 radius-sm p-4 mt-9">
              <Icon icon="icon-info" className="color-primary50 mt-1 me-2" />
              <span className="d-block">
                <p
                  dangerouslySetInnerHTML={{
                    __html: (
                      termsDescription || state?.dict?.custom?.termsInvestRent
                    )?.replaceAll("{{link}}", linkTerms),
                  }}
                ></p>
              </span>
            </div>
          </div>
        </Column>
        <Column lg={4} className="bg-gray10 color-gray90 radius-sm">
          <div
            className="d-flex justify-content-between p-5 h-100 simulator-totals"
            style={{ flexFlow: "column" }}
          >
            <div>
              <Heading size={"texts"} tag={"h4"} className="font-alternative mb-4">
                {state?.dict?.custom?.rentInMonths?.replaceAll(
                  "{{value}}",
                  String(totalMonths),
                )}
              </Heading>

              <Heading size={"h2"} tag={"span"} className="color-primary50 mb-4">
                {formatAsCurrency(totalReceived)}
              </Heading>

              <Heading size={"texts"} tag={"span"} className="font-alternative mb-4">
                {state?.dict?.custom?.rentUpfrontBonus?.replaceAll(
                  "{{value}}",
                  formatAsCurrency(upfrontBonus),
                )}
              </Heading>

              <Heading size={"texts"} tag={"span"} className="font-alternative mb-4">
                {state?.dict?.custom?.rentInstalmentsOf
                  ?.replaceAll("{{count}}", String(fields.numberOfRents))
                  .replaceAll("{{value}}", formatAsCurrency(periodPayment))}
              </Heading>

              <Separator color="var(--color-gray40)" height="1px" />
            </div>
            <div className="text-center mt-6">
              <Link
                href={
                  getStarted?.url
                    ? getStarted?.url
                    : "https://platform.daskapital.eu/register"
                }
                target="_blank"
                rel="noreferrer noopener"
                className="d-block"
              >
                <Button variant="primary" className="justify-content-center w-100">
                  {primaryButtonText || state.dict?.custom?.getStarted}
                </Button>
              </Link>
              <Link
                href={knowMore?.url ? knowMore?.url : "/contacts"}
                target="_blank"
                rel="noreferrer noopener"
                className="d-inline-block"
              >
                <Button variant="link">
                  {secondaryButtonText || state.dict?.common?.knowMore}
                </Button>
              </Link>
            </div>
          </div>
        </Column>
      </Row>
    </div>
  );
}
